// The badge's release list, as it comes back from the database.
//
// app_releases and app_release_changes are read once when the app shell mounts
// and handed down to the badge. This module is the pure part: it turns the rows
// into Release[] and decides when to fall back. Nothing here touches Supabase,
// so it can be tested without one.

import { RELEASES, compareVersions, isValidVersion, type ChangeKind, type Release } from './changelog'

/** One row of app_releases with its app_release_changes embedded. */
export interface AppReleaseRow {
  version: string
  released_on: string
  headline: string
  app_release_changes: Array<{
    position: number
    kind: string
    text: string
    roadmap_refs: string[] | null
  }> | null
}

const KINDS: ReadonlySet<string> = new Set<ChangeKind>(['added', 'improved', 'fixed'])

/**
 * Rows to releases, newest first.
 *
 * Sorted HERE with compareVersions, never in the query: ORDER BY version on a
 * text column puts '1.21.20' below '1.3.0'. Changes are sorted by position here
 * too, so the badge does not depend on PostgREST honouring a nested order. A
 * row whose version does not parse is dropped rather than shown.
 */
export function releasesFromRows(rows: AppReleaseRow[]): Release[] {
  const releases: Release[] = rows
    .filter((row) => isValidVersion(row.version))
    .map((row) => ({
      version: row.version,
      date: row.released_on,
      headline: row.headline,
      changes: [...(row.app_release_changes ?? [])]
        .sort((a, b) => a.position - b.position)
        .filter((c) => KINDS.has(c.kind))
        .map((c) => ({ kind: c.kind as ChangeKind, text: c.text, refs: c.roadmap_refs ?? [] })),
    }))
  return releases.sort((a, b) => compareVersions(b.version, a.version))
}

/**
 * What the badge should show. Database notes override matching versions,
 * while newer compiled releases remain visible before the database sync.
 * Compiled RELEASES also cover unavailable or empty database results.
 * The badge must never render blank because of the database.
 */
export function pickReleases(fromDb: Release[] | null | undefined, fallback: Release[] = RELEASES): Release[] {
  if (!fromDb?.length) return fallback
  // A successful deploy can precede the release-note DB sync. Keep the
  // compiled release visible; database notes still win for shared versions.
  const byVersion = new Map(fallback.map((release) => [release.version, release]))
  for (const release of fromDb) byVersion.set(release.version, release)
  return [...byVersion.values()].sort((a, b) => compareVersions(b.version, a.version))
}
